import { FC, useState } from 'react';
import { captureException } from '@sentry/react';
import { TbTrash } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import {
    dashboardService,
    folderService,
    investmentService,
    listService,
    memoService,
    pipelineService,
    teamService,
} from '@/services';
import DestructiveActionModal from '@/shared/components/modals/DestructiveActionModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { getWorkspaceResourceTypeLabel } from '@/shared/components/workspace-resources';
import {
    isRestorableWorkspaceResourceType,
    moveWorkspaceResourceToTrash,
    optimisticallyRestoreWorkspaceResource,
} from '@/shared/components/workspace-resources/optimisticRestore';
import { getWorkspaceResourceCollectionRoute } from '@/shared/workspaceResourceRoutes';
import {
    useConnectedAccountStore,
    useCustomFilterStore,
    useListViewColumnStore,
    useListViewStore,
    useReportComponentStore,
    useReportStore,
    useTeamStore,
} from '@/stores';
import { SetStateMethod } from '@/types';

interface DeletableWorkspaceResource {
    editedName?: string;
    id?: string;
    label?: string;
    name?: string;
    nickname?: string;
    title?: string;
}

interface DeleteResourceModalProps {
    onDelete?: () => Promise<unknown>;
    onDeleted?: () => void;
    resource?: DeletableWorkspaceResource;
    singularResource: string;
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

const DeleteResourceModal: FC<DeleteResourceModalProps> = ({
    resource,
    singularResource,
    open,
    setOpen,
    onDelete,
    onDeleted,
}): JSX.Element => {
    const [submitting, setSubmitting] = useState(false);
    const resourceTypeLabel = getWorkspaceResourceTypeLabel(singularResource);
    const resourceName =
        resource?.title ||
        resource?.name ||
        resource?.label ||
        resource?.editedName ||
        resource?.nickname ||
        resourceTypeLabel;
    const getResourceId = () => {
        if (!resource?.id) {
            throw new Error(`Select a ${resourceTypeLabel.toLowerCase()} before moving it to Trash.`);
        }

        return resource.id;
    };

    const navigate = useNavigate();

    const { deleteTeam, loadingTeam } = useTeamStore();
    const { deleteCustomFilter, loadingCustomFilter } = useCustomFilterStore();
    const { deleteConnectedAccount, loadingConnectedAccount } = useConnectedAccountStore();
    // Relations
    const { deleteReport, loadingReport } = useReportStore();
    const { deleteReportComponent, loadingReportComponent } = useReportComponentStore();
    const { deleteListView, loadingListView } = useListViewStore();
    const { deleteListViewColumn, loadingListViewColumn } = useListViewColumnStore();

    const functionMap: Record<string, () => Promise<unknown>> = {
        team: () => deleteTeam(getResourceId()),
        'custom-filter': () => deleteCustomFilter(getResourceId()),
        'connected-account': () => deleteConnectedAccount(getResourceId()),
        // Relations
        report: () => deleteReport(getResourceId()),
        'report-component': () => deleteReportComponent(getResourceId()),
        'list-view': () => deleteListView(getResourceId()),
        'list-view-column': () => deleteListViewColumn(getResourceId()),
    };
    const restoreFunctionMap: Record<string, (() => Promise<unknown>) | undefined> = {
        dashboard: () => dashboardService.restore(getResourceId()),
        folder: () => folderService.restore(getResourceId()),
        investment: () => investmentService.restore(getResourceId()),
        list: () => listService.restore(getResourceId()),
        memo: () => memoService.restore(getResourceId()),
        pipeline: () => pipelineService.restore(getResourceId()),
        team: () => teamService.restore(getResourceId()),
    };

    const errorMap: Record<string, { clear: () => void; get: () => string | undefined }> = {
        team: { clear: () => useTeamStore.setState({ error: undefined }), get: () => useTeamStore.getState().error },
        'custom-filter': {
            clear: () => useCustomFilterStore.setState({ error: undefined }),
            get: () => useCustomFilterStore.getState().error,
        },
        'connected-account': {
            clear: () => useConnectedAccountStore.setState({ error: undefined }),
            get: () => useConnectedAccountStore.getState().error,
        },
        report: {
            clear: () => useReportStore.setState({ error: undefined }),
            get: () => useReportStore.getState().error,
        },
        'report-component': {
            clear: () => useReportComponentStore.setState({ error: undefined }),
            get: () => useReportComponentStore.getState().error,
        },
        'list-view': {
            clear: () => useListViewStore.setState({ error: undefined }),
            get: () => useListViewStore.getState().error,
        },
        'list-view-column': {
            clear: () => useListViewColumnStore.setState({ error: undefined }),
            get: () => useListViewColumnStore.getState().error,
        },
    };

    const loading =
        submitting ||
        loadingReport ||
        loadingReportComponent ||
        loadingListView ||
        loadingListViewColumn ||
        loadingTeam ||
        loadingCustomFilter ||
        loadingConnectedAccount;

    const handleDeleteResource = async () => {
        const restorableResourceType = isRestorableWorkspaceResourceType(singularResource)
            ? singularResource
            : undefined;
        const deleteResource =
            onDelete ??
            (restorableResourceType
                ? () => moveWorkspaceResourceToTrash(restorableResourceType, getResourceId())
                : functionMap[singularResource]);
        const operationError = onDelete || restorableResourceType ? undefined : errorMap[singularResource];

        if (deleteResource && !loading) {
            try {
                setSubmitting(true);
                operationError?.clear();
                await deleteResource();

                const error = operationError?.get();

                if (error) {
                    throw new Error(error);
                }

                const restoreResource = onDelete ? undefined : restoreFunctionMap[singularResource];

                toast.warning(`${resourceTypeLabel} Moved to Trash`, {
                    description: resourceName,
                    descriptionKind: 'value',
                    icon: <TbTrash size='1rem' />,
                    undo: restoreResource
                        ? {
                              duration: 8000,
                              message: `${resourceTypeLabel} can be restored before this message closes.`,
                              onUndo: async () => {
                                  if (!restorableResourceType) {
                                      throw new Error(`${resourceTypeLabel} does not support Trash restoration.`);
                                  }

                                  const optimisticRestore = optimisticallyRestoreWorkspaceResource(
                                      restorableResourceType,
                                      {
                                          ...resource,
                                          id: getResourceId(),
                                      },
                                  );

                                  try {
                                      await restoreResource();
                                      // A route-level list request may have resolved with a pre-restore snapshot
                                      // while the API mutation was in flight. Re-commit against the latest store.
                                      optimisticRestore.commit();
                                      toast.success(`${resourceTypeLabel} Restored`, {
                                          description: resourceName,
                                          descriptionKind: 'value',
                                      });
                                  } catch (error) {
                                      optimisticRestore.rollback();
                                      captureException(error);
                                      toast.error(`Unable to Restore ${resourceTypeLabel}`, {
                                          description: `${resourceName} remains in Trash.`,
                                      });
                                  }
                              },
                          }
                        : undefined,
                });

                if (onDeleted) {
                    onDeleted();
                } else if (
                    singularResource !== 'custom-filter' &&
                    singularResource !== 'connected-account' &&
                    singularResource !== 'report' &&
                    singularResource !== 'report-component' &&
                    singularResource !== 'list-view' &&
                    singularResource !== 'list-view-column' &&
                    singularResource !== 'dashboard' &&
                    singularResource !== 'list'
                ) {
                    navigate(getWorkspaceResourceCollectionRoute(singularResource));
                }
            } catch (error) {
                captureException(error);
                throw error;
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleClose = () => {
        if (loading) {
            return;
        }

        setOpen(false);
    };

    return (
        <DestructiveActionModal
            actionLabel='Move to Trash'
            alertDescription={`${resourceName} will be removed from its current location. You can restore it from Trash until it is permanently deleted.`}
            alertTitle={`Move this ${resourceTypeLabel.toLowerCase()} to Trash?`}
            errorMessage={`Unable to move ${resourceTypeLabel.toLowerCase()} to Trash`}
            loading={loading}
            onCancel={handleClose}
            onConfirm={handleDeleteResource}
            open={open}
            severity='warning'
            title={`Move ${resourceTypeLabel} to Trash`}
        />
    );
};

export default DeleteResourceModal;
